/**
 * The artwork behind {@code ProductThumb}'s no-photograph plate.
 *
 * <p>A copy of the storefront's `photoPlaceholderArt`, deliberately: the two live in separate
 * published packages and neither may import the other. Keep them in step by eye — they are
 * pure drawing with no behaviour, so drift shows up immediately as a grid that does not match
 * between the coded routes and the site-builder blocks.
 *
 * <p>These are FILLED silhouettes on a lit ground, not line icons. That distinction is the
 * whole point of the file: an outline glyph reads as an icon — a piece of interface — and a
 * grid of them still tells nobody what the shop looks like once photography lands. A filled
 * object with a shadow under it reads, at a glance and at card size, as a thing photographed
 * on white, which is exactly what a supplier's product shot is. Up close it is obviously a
 * drawing, so it claims nothing about the part it stands for.
 *
 * <p>Everything is drawn in {@code currentColor} at varying opacity, so one tone per plate
 * keeps a grid coherent the way a consistent lighting setup does across a real catalogue.
 */

/** A module: the angled face, its cell grid, and the frame edge along the bottom. */
export function PanelArt() {
  return (
    <>
      <ellipse cx="60" cy="100" rx="38" ry="5" fill="currentColor" opacity="0.14" />
      <path d="M22 30h76l8 62H14z" fill="currentColor" opacity="0.92" />
      <path
        d="M34 30 28 92M60 30v62M86 30l6 62M19 50h82M17 71h86"
        stroke="#fff"
        strokeWidth="2"
        opacity="0.35"
      />
      <path d="M14 92h92v6H14z" fill="currentColor" opacity="0.55" />
    </>
  );
}

/** An inverter or consumer unit: a wall box with a vent band and a status lamp. */
export function EnclosureArt() {
  return (
    <>
      <ellipse cx="60" cy="102" rx="32" ry="5" fill="currentColor" opacity="0.14" />
      <rect x="30" y="16" width="60" height="84" rx="8" fill="currentColor" opacity="0.9" />
      <rect x="40" y="30" width="40" height="16" rx="3" fill="#fff" opacity="0.32" />
      <circle cx="60" cy="72" r="9" fill="#fff" opacity="0.28" />
      <rect x="46" y="94" width="28" height="4" rx="2" fill="#fff" opacity="0.3" />
    </>
  );
}

/** Cable on a drum: the coil most of this catalogue's length-goods ship as. */
export function CoilArt() {
  return (
    <>
      <ellipse cx="60" cy="102" rx="34" ry="5" fill="currentColor" opacity="0.14" />
      <path
        d="M60 22a38 38 0 1 1 0 76 38 38 0 0 1 0-76Zm0 26a12 12 0 1 0 0 24 12 12 0 0 0 0-24Z"
        fill="currentColor"
        opacity="0.9"
        fillRule="evenodd"
      />
      <path d="M60 22a38 38 0 0 1 34 21" stroke="#fff" strokeWidth="3" opacity="0.3" fill="none" />
      <path
        d="M94 43c8-4 14-1 16 5"
        stroke="currentColor"
        strokeWidth="5"
        strokeLinecap="round"
        opacity="0.75"
        fill="none"
      />
    </>
  );
}

/** A battery cabinet: the tall stack, banded, that storage ships as. */
export function BatteryArt() {
  return (
    <>
      <ellipse cx="60" cy="104" rx="28" ry="5" fill="currentColor" opacity="0.14" />
      <rect x="34" y="14" width="52" height="88" rx="7" fill="currentColor" opacity="0.9" />
      <rect x="34" y="38" width="52" height="4" fill="#fff" opacity="0.32" />
      <rect x="34" y="64" width="52" height="4" fill="#fff" opacity="0.32" />
      <rect x="46" y="20" width="28" height="10" rx="3" fill="#fff" opacity="0.28" />
    </>
  );
}

/** Mounting rail with its end brackets — the metalwork that holds a roof array down. */
export function RailArt() {
  return (
    <>
      <ellipse cx="60" cy="96" rx="40" ry="5" fill="currentColor" opacity="0.14" />
      <rect x="10" y="52" width="100" height="16" rx="4" fill="currentColor" opacity="0.9" />
      <rect x="22" y="66" width="16" height="24" rx="3" fill="currentColor" opacity="0.7" />
      <rect x="82" y="66" width="16" height="24" rx="3" fill="currentColor" opacity="0.7" />
      <rect x="10" y="56" width="100" height="3" fill="#fff" opacity="0.3" />
    </>
  );
}

/** A connector pair with its tail — the small parts that fill most of an order's lines. */
export function ConnectorArt() {
  return (
    <>
      <ellipse cx="60" cy="98" rx="30" ry="5" fill="currentColor" opacity="0.14" />
      <rect x="40" y="34" width="40" height="56" rx="14" fill="currentColor" opacity="0.9" />
      <rect x="48" y="44" width="24" height="6" rx="3" fill="#fff" opacity="0.32" />
      <path
        d="M60 34V18c0-6 6-9 12-7"
        stroke="currentColor"
        strokeWidth="6"
        strokeLinecap="round"
        opacity="0.75"
        fill="none"
      />
    </>
  );
}

export const PHOTO_PLACEHOLDER_ART = [
  PanelArt,
  EnclosureArt,
  CoilArt,
  BatteryArt,
  RailArt,
  ConnectorArt,
];

/**
 * The tonal range this trade's photography actually occupies — panel navy, frame silver,
 * enclosure grey, cable black — rather than a spread of hues, which reads as a colour chart.
 */
export const PHOTO_PLACEHOLDER_TONES = [
  '#1e3a5f',
  '#475569',
  '#8fa3b8',
  '#334155',
  '#64748b',
  '#0f172a',
];

/**
 * djb2, trimmed to a positive int. Stable across server and browser, unlike anything random,
 * so a product keeps its plate through hydration and between visits.
 *
 * <p>`salt` picks a different stream from the same seed: tone and motif are chosen
 * independently, so two products sharing a tone rarely share a shape as well.
 */
export function photoPlaceholderHash(value: string, salt = ''): number {
  let h = 5381;
  const text = `${salt}:${value}:${salt}`;
  for (let i = 0; i < text.length; i += 1) h = ((h << 5) + h + text.charCodeAt(i)) | 0;
  return Math.abs(h);
}
